import { Component, OnInit, ViewChild } from '@angular/core';
import { NfcReaderService } from '../../services/nfc-reader.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import * as moment from 'moment';

@Component({
  selector: 'app-nfc-monitoring',
  templateUrl: './nfc-monitoring.component.html',
  styleUrls: ['./nfc-monitoring.component.scss']
})
export class NfcMonitoringComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  
  statusData: any[] = [];
  timeSeriesData: any[] = [];
  storeStatusCounts: any[] = [];
  nebularChartData: any[] = [];
  summary: any = {
    totalReaders: 0,
    onlineReaders: 0,
    offlineReaders: 0,
    uptimePercentage: 0,
    asOf: ''
  };
  historical: any = {
    uptimePercentage: 0,
    totalReadings: 0,
    onlineReadings: 0
  };
  dateRange: any = {};
  isLoading = false;
  selectedInterval = '15min';
  startDate: string = '';
  endDate: string = '';
  date: any;
  displayChart = false;

  // Chart configuration
  public lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [],
        label: 'Online Readers',
        backgroundColor: 'rgba(75, 192, 75, 0.2)',
        borderColor: 'rgba(75, 192, 75, 1)',
        pointBackgroundColor: 'rgba(75, 192, 75, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(75, 192, 75, 1)',
        fill: 'origin',
      },
      {
        data: [],
        label: 'Offline Readers',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(255, 99, 132, 1)',
        fill: 'origin',
      },
      {
        data: [],
        label: 'Total Readers',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)',
        fill: 'origin',
        borderDash: [5, 5], // Make the total line dashed for better visibility
      }
    ],
    labels: []
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Readers'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      }
    },
    plugins: {
      legend: { display: true }
    }
  };
  
  public lineChartType: ChartType = 'line';

  constructor(
    private nfcReaderService: NfcReaderService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {
    // Set default date range (7 days ago to today)
    this.startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
    this.endDate = moment().format('YYYY-MM-DD');
  }

  ngOnInit(): void {
    this.loadStatusData();
  }

  clearDate() {
    this.startDate = '';
    this.endDate = '';
    this.date = '';
    this.loadStatusData();
  }

  updateRangeDate(event: any) {
    if (event.start && event.end) {
      this.startDate = moment(event.start).startOf('day').format('YYYY-MM-DD');
      this.endDate = moment(event.end).endOf('day').format('YYYY-MM-DD');
      this.loadStatusData();
    }
  }

  loadStatusData(): void {
    this.isLoading = true;
    this.spinner.show();
    
    this.nfcReaderService.getAllStoresStatusData(this.startDate, this.endDate, this.selectedInterval).subscribe({
      next: (response) => {
        if (response.response && response.response.success) {
          // Updated to match new API response structure
          const data = response.response.data;
          this.summary = data.summary || this.summary;
          this.historical = data.historical || this.historical;
          this.timeSeriesData = data.historical?.timeSeriesData || [];
          this.storeStatusCounts = data.historical?.storeStatusCounts || [];
          this.statusData = data.statusData || [];
          this.dateRange = data.dateRange;
          // Store the nebularChartData if available
          this.nebularChartData = data.nebularChartData || [];
          this.updateChartData();
        } else {
          this.toastr.error(response.response?.message || 'Unknown error', 'Error');
        }
        this.isLoading = false;
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error fetching NFC reader data:', error);
        this.toastr.error('Failed to load NFC reader data', 'Error');
        this.isLoading = false;
        this.spinner.hide();
      }
    });
  }

  updateChartData(): void {
    // Use the nebularChartData if available
    if (this.nebularChartData && this.nebularChartData.length > 0) {
      // Extract time labels from the first series
      const timestamps: string[] = [];
      const onlineData: number[] = [];
      const offlineData: number[] = [];
      const totalData: number[] = [];
      
      // Find each series by name
      const onlineSeries = this.nebularChartData.find(item => item.name === 'Online Readers')?.series || [];
      const offlineSeries = this.nebularChartData.find(item => item.name === 'Offline Readers')?.series || [];
      const totalSeries = this.nebularChartData.find(item => item.name === 'Total Readers')?.series || [];
      
      // Get all unique timestamps
      const allTimestamps = new Set<string>();
      onlineSeries.forEach((item: {name: string, value: number}) => allTimestamps.add(item.name));
      offlineSeries.forEach((item: {name: string, value: number}) => allTimestamps.add(item.name));
      totalSeries.forEach((item: {name: string, value: number}) => allTimestamps.add(item.name));
      
      // Sort timestamps
      const sortedTimestamps = Array.from(allTimestamps).sort();
      
      // Map each series to the sorted timestamps
      sortedTimestamps.forEach(timestamp => {
        // Format the timestamp for display
        const date = new Date(timestamp);
        const formattedDate = `${date.toISOString().slice(0, 16).replace('T', ' ')}`;
        timestamps.push(formattedDate);
        
        // Get values or default to 0
        const onlineItem = onlineSeries.find((item: {name: string, value: number}) => item.name === timestamp);
        const offlineItem = offlineSeries.find((item: {name: string, value: number}) => item.name === timestamp);
        const totalItem = totalSeries.find((item: {name: string, value: number}) => item.name === timestamp);
        
        onlineData.push(onlineItem?.value || 0);
        offlineData.push(offlineItem?.value || 0);
        totalData.push(totalItem?.value || 0);
      });
      
      // Update chart data
      this.lineChartData.labels = timestamps;
      if (this.lineChartData.datasets && this.lineChartData.datasets.length >= 3) {
        this.lineChartData.datasets[0].data = onlineData;
        this.lineChartData.datasets[1].data = offlineData;
        this.lineChartData.datasets[2].data = totalData;
      }
    } else {
      // Fall back to the previous implementation using timeSeriesData
      const timestamps: string[] = [];
      const onlineData: number[] = [];
      const offlineData: number[] = [];
      const totalData: number[] = [];

      if (this.timeSeriesData && this.timeSeriesData.length > 0) {
        // Sort by timestamp
        const sortedData = [...this.timeSeriesData].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        
        sortedData.forEach(item => {
          // Format timestamp as string
          const date = new Date(item.timestamp);
          const formattedDate = `${date.toISOString().slice(0, 16).replace('T', ' ')}`;
          timestamps.push(formattedDate);
          
          // Extract values
          onlineData.push(item.onlineCount || 0);
          offlineData.push(item.offlineCount || 0);
          totalData.push(item.totalCount || (item.onlineCount + item.offlineCount) || 0);
        });
      }

      // Update chart data
      this.lineChartData.labels = timestamps;
      if (this.lineChartData.datasets && this.lineChartData.datasets.length >= 3) {
        this.lineChartData.datasets[0].data = onlineData;
        this.lineChartData.datasets[1].data = offlineData;
        this.lineChartData.datasets[2].data = totalData;
      }
    }

    this.displayChart = true;
    
    // Update chart
    if (this.chart) {
      this.chart.update();
    }
  }

  onIntervalChange(event: any): void {
    this.selectedInterval = event.target.value;
    this.loadStatusData();
  }

  // Updated methods to use the new data structure
  getOnlineStoresCount(): number {
    return this.summary.onlineReaders || 0;
  }

  getOfflineStoresCount(): number {
    return this.summary.offlineReaders || 0;
  }

  getTotalStoresCount(): number {
    return this.summary.totalReaders || 0;
  }

  getOnlinePercentage(): number {
    return this.summary.uptimePercentage || 0;
  }

  getLastUpdated(): string {
    return this.summary.asOf ? moment(this.summary.asOf).format('YYYY-MM-DD HH:mm:ss') : 'N/A';
  }

  // Historical data getters for potential future use
  getHistoricalUptime(): number {
    return this.historical.uptimePercentage || 0;
  }

  triggerMonitoring(): void {
    this.spinner.show();
    this.nfcReaderService.triggerNfcMonitoring().subscribe({
      next: (response) => {
        if (response.response && response.response.success) {
          this.toastr.success('NFC reader monitoring check completed', 'Success');
          // Reload data after monitoring is triggered
          this.loadStatusData();
        } else {
          this.toastr.error(response.response?.message || 'Unknown error', 'Error');
        }
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error triggering NFC monitoring:', error);
        this.toastr.error('Failed to trigger NFC monitoring', 'Error');
        this.spinner.hide();
      }
    });
  }
} 